/**
 * PremiumKit.js
 *
 * Building blocks of the Happy Services premium design language (orange brand,
 * airy white cards, soft tinted chips, one bold gradient highlight per screen):
 *
 *  - {@link IconChip}, {@link DeltaPill}, {@link RoundIconButton} — atoms;
 *  - {@link SectionHeader}, {@link PillTabs}                       — navigation text;
 *  - {@link HeroKpiCard}, {@link GlassTile}                        — the gradient highlight card;
 *  - {@link RankedRow}                                             — "recent sales"-style rows;
 *  - {@link ActionTile}                                            — square shortcut tile (module / action grids);
 *  - {@link GreetingHeader}                                        — personal dashboard header.
 *
 * Presentation only — callers own data, navigation and gating. Everything is
 * theme-aware via `useManagementTheme` (light + dark).
 *
 * @module components/premium/PremiumKit
 */

import React from 'react';
import {Pressable, ScrollView, StyleSheet, Text, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import Icon from '../management/SafeIcon';
import Avatar from '../Avatar';
import {BRAND, useManagementTheme} from '../../helpers/managementTheme';
import {Sparkline} from './PremiumCharts';
import {haptics, withHaptic} from '../../helpers/haptics';
import WorkdayHeaderPill from '../workday/WorkdayHeaderPill';

/**
 * Soft tinted icon chip — the calm replacement for the old saturated gradient
 * squares. Colour comes from the tone, the fill is the tone at ~12 % alpha.
 *
 * @param {object} props
 * @param {string} props.icon MDI glyph.
 * @param {string} [props.tone='primary'] Tone key (primary/success/info/violet/danger/warning/neutral).
 * @param {number} [props.size=42] Chip edge in px.
 * @param {boolean} [props.round=false] Circle instead of a rounded square.
 * @param {boolean} [props.solid=false] Solid tone fill with a white glyph (selected / emphasised state).
 * @param {object} [props.style] Extra container style.
 * @returns {JSX.Element} The chip.
 */
export const IconChip = ({icon, tone = 'primary', size = 42, round = false, solid = false, style}) => {
    const m = useManagementTheme();
    const color = m.toneColor(tone);

    return (
        <View
            style={[
                styles.iconChip,
                {
                    width: size,
                    height: size,
                    borderRadius: round ? size / 2 : Math.round(size * 0.34),
                    backgroundColor: solid ? color : m.toneTint(tone),
                },
                style,
            ]}
        >
            <Icon name={icon} size={Math.round(size * 0.5)} color={solid ? '#FFFFFF' : color} />
        </View>
    );
};

/**
 * Trend pill (▲ +12 %). Green up / red down / grey flat on light surfaces, a
 * translucent white pill on gradient (`onDark`) surfaces.
 *
 * @param {object} props
 * @param {number} props.delta Percentage change (sign decides direction).
 * @param {boolean} [props.onDark=false] Render for use on the orange hero card.
 * @param {string} [props.suffix='%'] Unit appended to the rounded number.
 * @returns {?JSX.Element} The pill, or `null` for a non-numeric delta.
 */
export const DeltaPill = ({delta, onDark = false, suffix = '%'}) => {
    const m = useManagementTheme();
    const value = Number(delta);

    if (!Number.isFinite(value)) {
        return null;
    }

    const up = value > 0;
    const flat = Math.round(value) === 0;
    const tone = flat ? m.colors.subText : up ? m.colors.success : m.colors.danger;
    const fg = onDark ? '#FFFFFF' : tone;
    const bg = onDark ? 'rgba(255,255,255,0.24)' : m.toneTint(flat ? 'neutral' : up ? 'success' : 'danger');

    return (
        <View style={[styles.delta, {backgroundColor: bg}]}>
            <Icon name={flat ? 'minus' : up ? 'arrow-top-right' : 'arrow-bottom-right'} size={12} color={fg} />
            <Text style={[styles.deltaText, {color: fg}]}>{`${up ? '+' : ''}${Math.round(value)}${suffix}`}</Text>
        </View>
    );
};

/**
 * Round 44 px icon button with optional unread dot — header actions (bell, chat…).
 *
 * @param {object} props
 * @param {string} props.icon MDI glyph.
 * @param {Function} [props.onPress] Tap handler.
 * @param {boolean} [props.dot=false] Show the orange notification dot.
 * @param {number} [props.size=44] Button diameter.
 * @param {string} [props.accessibilityLabel] Screen-reader label.
 * @param {boolean} [props.dark=false] Translucent variant for dark / gradient canvases.
 * @param {boolean} [props.disabled=false] Dim the button and ignore presses.
 * @returns {JSX.Element} The button.
 */
export const RoundIconButton = ({icon, onPress, dot = false, size = 44, accessibilityLabel, dark = false, disabled = false}) => {
    const m = useManagementTheme();

    return (
        <Pressable
            onPress={withHaptic(onPress, 'select')}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
            accessibilityState={{disabled}}
            style={({pressed}) => [
                styles.roundBtn,
                {width: size, height: size, borderRadius: size / 2, opacity: disabled ? 0.45 : (pressed ? 0.85 : 1)},
                dark
                    ? {backgroundColor: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.16)', borderWidth: 1}
                    : [{backgroundColor: m.colors.surface, borderColor: m.colors.border, borderWidth: 1}, m.shadow(1)],
            ]}
        >
            <Icon name={icon} size={Math.round(size * 0.46)} color={dark ? '#FFFFFF' : m.colors.heading} />
            {dot && <View style={[styles.roundBtnDot, {borderColor: dark ? '#0B1B44' : m.colors.surface}]} />}
        </Pressable>
    );
};

/**
 * Section heading: bold sentence-case title on the left, orange "See all ›" on
 * the right.
 *
 * @param {object} props
 * @param {string} props.title Heading.
 * @param {string} [props.actionLabel] Right-hand link text.
 * @param {Function} [props.onPress] Link handler.
 * @param {string} [props.icon] Optional glyph rendered as a small tinted chip before the title.
 * @param {string} [props.tone='primary'] Chip tone.
 * @param {boolean} [props.dark=false] White text for dark canvases.
 * @param {object} [props.style] Extra row style (e.g. tighter margins).
 * @returns {JSX.Element} The heading row.
 */
export const SectionHeader = ({title, actionLabel, onPress, icon, tone = 'primary', dark = false, style}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.sectionRow, style]}>
            <View style={styles.sectionLeft}>
                {!!icon && <IconChip icon={icon} tone={tone} size={28} round style={styles.sectionChip} />}
                <Text style={[styles.sectionTitle, {color: dark ? '#FFFFFF' : m.colors.heading}]} numberOfLines={1}>{title}</Text>
            </View>
            {!!actionLabel && (
                <Pressable onPress={onPress} hitSlop={8} style={styles.sectionAction} accessibilityRole="button">
                    <Text style={[styles.sectionActionText, {color: dark ? '#FDBA74' : m.colors.primary}]}>{actionLabel}</Text>
                    <Icon name="chevron-right" size={16} color={dark ? '#FDBA74' : m.colors.primary} />
                </Pressable>
            )}
        </View>
    );
};

/**
 * Text tabs (Week · Month · Year). `soft` marks the active tab with an orange
 * tinted chip, `solid` with a filled orange pill.
 *
 * @param {object} props
 * @param {Array<{value: *, label: string, icon?: string}>} props.options Tabs.
 * @param {*} props.value Active option value.
 * @param {Function} props.onChange `(value) => void`.
 * @param {'soft'|'solid'} [props.variant='soft'] Active-tab treatment.
 * @param {boolean} [props.scroll=false] Horizontally scrollable (many tabs).
 * @param {boolean} [props.dark=false] White inactive text for dark canvases.
 * @returns {JSX.Element} The tab row.
 */
export const PillTabs = ({options = [], value, onChange, variant = 'soft', scroll = false, dark = false}) => {
    const m = useManagementTheme();
    const items = options.map((option) => {
        const active = option.value === value;
        const solid = variant === 'solid';

        return (
            <Pressable
                key={String(option.value)}
                onPress={() => {
                    if (!active) {
                        haptics.select();
                    }
                    onChange?.(option.value);
                }}
                accessibilityRole="tab"
                accessibilityState={{selected: active}}
                style={[
                    styles.pillTab,
                    active && (solid ? {backgroundColor: BRAND.base} : {backgroundColor: dark ? 'rgba(249,115,22,0.22)' : m.colors.chipBg}),
                ]}
            >
                {!!option.icon && <Icon name={option.icon} size={15} color={active ? (solid ? '#FFFFFF' : m.colors.primary) : (dark ? 'rgba(255,255,255,0.7)' : m.colors.subText)} />}
                <Text
                    style={[
                        styles.pillTabText,
                        {color: active ? (solid ? '#FFFFFF' : m.colors.primary) : (dark ? 'rgba(255,255,255,0.7)' : m.colors.subText), fontWeight: active ? '800' : '600'},
                    ]}
                    numberOfLines={1}
                >
                    {option.label}
                </Text>
            </Pressable>
        );
    });

    if (scroll) {
        return (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.pillTabs} accessibilityRole="tablist">
                {items}
            </ScrollView>
        );
    }

    return <View style={styles.pillTabs} accessibilityRole="tablist">{items}</View>;
};

/**
 * Frosted white tile that lives on the orange hero card (label, value, glyph).
 *
 * @param {object} props
 * @param {string} props.label Caption.
 * @param {string|number} props.value Bold value.
 * @param {string} [props.icon] MDI glyph.
 * @param {Function} [props.onPress] Tap handler.
 * @returns {JSX.Element} The tile.
 */
export const GlassTile = ({label, value, icon, onPress}) => (
    <Pressable
        onPress={onPress}
        disabled={!onPress}
        style={({pressed}) => [styles.glassTile, pressed && {opacity: 0.85}]}
    >
        <View style={styles.glassTileTop}>
            {!!icon && <Icon name={icon} size={14} color="rgba(255,255,255,0.92)" />}
            <Text style={styles.glassTileLabel} numberOfLines={2}>{label}</Text>
        </View>
        <Text style={styles.glassTileValue} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7}>{value == null ? '' : String(value)}</Text>
    </Pressable>
);

/**
 * The one gradient highlight per screen: a big headline figure with its trend,
 * a sparkline and up to four frosted tiles.
 *
 * @param {object} props
 * @param {string} props.label Caption above the figure.
 * @param {string|number} props.value Headline figure (already formatted).
 * @param {number} [props.delta] Percentage change shown as a pill.
 * @param {string} [props.caption] Muted line under the figure.
 * @param {Array<number|{value: number}>} [props.series] Trend values for the sparkline.
 * @param {Array<{key?: string, label: string, value: string|number, icon?: string, onPress?: Function}>} [props.tiles] Frosted tiles.
 * @param {Function} [props.onPress] Opens the figure's detail (renders a ↗ button).
 * @param {React.ReactNode} [props.footer] Extra content under the tiles.
 * @returns {JSX.Element} The card.
 */
export const HeroKpiCard = ({label, value, delta, caption, series, tiles = [], onPress, footer}) => {
    const m = useManagementTheme();

    return (
        <LinearGradient
            colors={m.gradients.brand}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 1}}
            style={[styles.hero, {shadowColor: BRAND.dark}]}
        >
            <View pointerEvents="none" style={styles.heroBlobA} />
            <View pointerEvents="none" style={styles.heroBlobB} />

            <View style={styles.heroTop}>
                <Text style={styles.heroLabel} numberOfLines={1}>{label}</Text>
                {!!onPress && (
                    <Pressable onPress={onPress} hitSlop={8} accessibilityRole="button" style={styles.heroArrow}>
                        <Icon name="arrow-top-right" size={16} color="#FFFFFF" />
                    </Pressable>
                )}
            </View>

            <View style={styles.heroValueRow}>
                <Text style={styles.heroValue} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>{value == null ? '' : String(value)}</Text>
                {delta != null && <DeltaPill delta={delta} onDark />}
            </View>
            {!!caption && <Text style={styles.heroCaption} numberOfLines={1}>{caption}</Text>}

            {!!series?.length && (
                <View style={styles.heroSpark}>
                    <Sparkline data={series} color="#FFFFFF" dotRing={BRAND.light} height={58} strokeWidth={2.4} />
                </View>
            )}

            {tiles.length > 0 && (
                <View style={styles.heroTiles}>
                    {tiles.map((tile, index) => (
                        <View key={tile.key || `${tile.label}-${index}`} style={styles.heroTileCell}>
                            <GlassTile label={tile.label} value={tile.value} icon={tile.icon} onPress={tile.onPress} />
                        </View>
                    ))}
                </View>
            )}
            {footer}
        </LinearGradient>
    );
};

/**
 * Ranked / recent-activity row. `highlight` turns it into the orange pill of the
 * "Recent sales" concept; otherwise a calm tinted pill.
 *
 * @param {object} props
 * @param {string} props.title Primary text.
 * @param {string} [props.subtitle] Secondary text.
 * @param {string} [props.meta] Right-hand figure (amount, quantity…).
 * @param {string} [props.metaCaption] Small text under the figure.
 * @param {string} [props.icon] Leading glyph (used when there is no `avatarName`).
 * @param {string} [props.avatarName] Person / company name → initials avatar.
 * @param {string} [props.tone='primary'] Icon-chip tone.
 * @param {boolean} [props.highlight=false] Orange emphasised row.
 * @param {React.ReactNode} [props.badge] Optional element between text and figure (e.g. a StatusBadge).
 * @param {Function} [props.onPress] Tap handler.
 * @returns {JSX.Element} The row.
 */
export const RankedRow = ({title, subtitle, meta, metaCaption, icon, avatarName, tone = 'primary', highlight = false, badge, onPress}) => {
    const m = useManagementTheme();
    const body = (
        <>
            {avatarName ? (
                <Avatar user={avatarName} size={42} />
            ) : (
                <IconChip icon={icon || 'receipt-text-outline'} tone={tone} size={42} round solid={highlight} style={highlight && {backgroundColor: 'rgba(255,255,255,0.24)'}} />
            )}
            <View style={styles.rankedText}>
                <Text style={[styles.rankedTitle, {color: highlight ? '#FFFFFF' : m.colors.heading}]} numberOfLines={1}>{title}</Text>
                {!!subtitle && <Text style={[styles.rankedSub, {color: highlight ? 'rgba(255,255,255,0.86)' : m.colors.subText}]} numberOfLines={1}>{subtitle}</Text>}
            </View>
            {badge}
            {(meta != null && meta !== '') && (
                <View style={styles.rankedEnd}>
                    <Text style={[styles.rankedMeta, {color: highlight ? '#FFFFFF' : m.colors.heading}]} numberOfLines={1}>{meta}</Text>
                    {!!metaCaption && <Text style={[styles.rankedSub, {color: highlight ? 'rgba(255,255,255,0.86)' : m.colors.subText}]} numberOfLines={1}>{metaCaption}</Text>}
                </View>
            )}
        </>
    );

    if (highlight) {
        return (
            <Pressable onPress={onPress} disabled={!onPress} style={({pressed}) => [{opacity: pressed ? 0.92 : 1}, styles.rankedShadow]}>
                <LinearGradient colors={m.gradients.brand} start={{x: 0, y: 0}} end={{x: 1, y: 0}} style={[styles.rankedRow, styles.rankedFlush]}>
                    {body}
                </LinearGradient>
            </Pressable>
        );
    }

    return (
        <Pressable
            onPress={onPress}
            disabled={!onPress}
            style={({pressed}) => [styles.rankedRow, {backgroundColor: m.colors.surfaceAlt, opacity: pressed ? 0.88 : 1}]}
        >
            {body}
        </Pressable>
    );
};

/**
 * Square-ish shortcut tile: tinted chip on top, title + optional subtitle below.
 * Lays out two-up by default; locked tiles render muted with their reason.
 *
 * @param {object} props
 * @param {string} props.icon MDI glyph.
 * @param {string} props.title Tile title.
 * @param {string} [props.subtitle] One-line description.
 * @param {string} [props.tone='primary'] Chip tone.
 * @param {Function} [props.onPress] Tap handler (ignored while disabled).
 * @param {boolean} [props.disabled=false] Locked state.
 * @param {string} [props.lockedReason] Why the tile is locked.
 * @param {string|number} [props.width='48.5%'] Tile width.
 * @param {number|string} [props.badge] Unread / count badge shown on the chip corner.
 * @returns {JSX.Element} The tile.
 */
export const ActionTile = ({icon, title, subtitle, tone = 'primary', onPress, disabled = false, lockedReason, width = '48.5%', badge}) => {
    const m = useManagementTheme();

    return (
        <Pressable
            onPress={disabled ? undefined : withHaptic(onPress, 'tap')}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityState={{disabled}}
            style={({pressed}) => [
                styles.actionTile,
                {width, backgroundColor: m.colors.surface, borderColor: m.colors.border, opacity: disabled ? 0.55 : (pressed ? 0.9 : 1)},
                !disabled && pressed && {transform: [{scale: 0.98}]},
                m.shadow(1),
            ]}
        >
            <View style={styles.actionTileTop}>
                <View>
                    <IconChip icon={disabled ? 'lock-outline' : icon} tone={disabled ? 'neutral' : tone} size={44} />
                    {badge != null && badge !== 0 && badge !== '' && (
                        <View style={styles.tileBadge}>
                            <Text style={styles.tileBadgeText} numberOfLines={1}>{typeof badge === 'number' && badge > 99 ? '99+' : badge}</Text>
                        </View>
                    )}
                </View>
                <Icon name="arrow-top-right" size={18} color={m.colors.muted} />
            </View>
            <Text style={[styles.actionTileTitle, {color: m.colors.heading}]} numberOfLines={2}>{title}</Text>
            {!!subtitle && !disabled && <Text style={[styles.actionTileSub, {color: m.colors.subText}]} numberOfLines={2}>{subtitle}</Text>}
            {disabled && !!lockedReason && <Text style={[styles.actionTileSub, {color: m.colors.muted}]} numberOfLines={2}>{lockedReason}</Text>}
        </Pressable>
    );
};

/**
 * Personal dashboard header: avatar + scope chip + action buttons on the first
 * line, then eyebrow / big name / subtitle.
 *
 * @param {object} props
 * @param {object|string} [props.user] User object or display name for the avatar.
 * @param {Function} [props.onAvatarPress] Avatar tap (profile / settings).
 * @param {string} [props.eyebrow] Small line above the title (greeting).
 * @param {string} props.title Big heading (name or screen title).
 * @param {string} [props.subtitle] Muted line under the title.
 * @param {string} [props.chipLabel] Scope / store chip text beside the avatar.
 * @param {string} [props.chipIcon='storefront-outline'] Glyph of the chip.
 * @param {React.ReactNode} [props.right] Action buttons (right-aligned).
 * @param {boolean} [props.dark=false] White text for dark canvases.
 * @returns {JSX.Element} The header.
 */
export const GreetingHeader = ({user, onAvatarPress, eyebrow, title, subtitle, chipLabel, chipIcon = 'storefront-outline', right, dark = false}) => {
    const m = useManagementTheme();
    const heading = dark ? '#FFFFFF' : m.colors.heading;
    const sub = dark ? 'rgba(226,236,255,0.72)' : m.colors.subText;

    return (
        <View style={styles.greeting}>
            <View style={styles.greetingTop}>
                <Pressable onPress={onAvatarPress} disabled={!onAvatarPress} accessibilityRole="button" style={[styles.avatarRing, {borderColor: m.colors.primary}]}>
                    <Avatar user={user} size={40} />
                </Pressable>
                {!!chipLabel && (
                    <View style={[styles.scopeChip, {backgroundColor: dark ? 'rgba(255,255,255,0.1)' : m.colors.surface, borderColor: dark ? 'rgba(255,255,255,0.14)' : m.colors.border}]}>
                        <Icon name={chipIcon} size={14} color={m.colors.primary} />
                        <Text style={[styles.scopeChipText, {color: dark ? '#FFFFFF' : m.colors.text}]} numberOfLines={1}>{chipLabel}</Text>
                    </View>
                )}
                <View style={styles.greetingSpacer} />
                {/* Munkanap pill: in front of the screen's own action buttons on every dashboard header. */}
                <WorkdayHeaderPill onDark={dark} style={styles.greetingPill} compact />
                {right}
            </View>
            {!!eyebrow && <Text style={[styles.greetingEyebrow, {color: sub}]} numberOfLines={1}>{eyebrow}</Text>}
            <Text style={[styles.greetingTitle, {color: heading}]} numberOfLines={2}>{title}</Text>
            {!!subtitle && <Text style={[styles.greetingSub, {color: sub}]} numberOfLines={2}>{subtitle}</Text>}
        </View>
    );
};

const styles = StyleSheet.create({
    greetingPill: {marginRight: 8},
    iconChip: {alignItems: 'center', justifyContent: 'center'},

    delta: {flexDirection: 'row', alignItems: 'center', gap: 2, borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3},
    deltaText: {fontSize: 11.5, fontWeight: '800'},

    roundBtn: {alignItems: 'center', justifyContent: 'center'},
    roundBtnDot: {position: 'absolute', top: 10, right: 11, width: 9, height: 9, borderRadius: 5, backgroundColor: BRAND.base, borderWidth: 1.5},

    sectionRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 22, marginBottom: 12},
    sectionLeft: {flexDirection: 'row', alignItems: 'center', flex: 1, paddingRight: 8},
    sectionChip: {marginRight: 8},
    sectionTitle: {fontSize: 18, fontWeight: '800', letterSpacing: -0.2, flexShrink: 1},
    sectionAction: {flexDirection: 'row', alignItems: 'center'},
    sectionActionText: {fontSize: 13.5, fontWeight: '700'},

    pillTabs: {flexDirection: 'row', alignItems: 'center', gap: 4},
    pillTab: {flexDirection: 'row', alignItems: 'center', gap: 5, borderRadius: 999, paddingHorizontal: 14, paddingVertical: 7},
    pillTabText: {fontSize: 13.5},

    glassTile: {borderRadius: 20, paddingVertical: 12, paddingHorizontal: 14, backgroundColor: 'rgba(255,255,255,0.20)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.30)'},
    glassTileTop: {flexDirection: 'row', alignItems: 'center', gap: 6},
    glassTileLabel: {color: 'rgba(255,255,255,0.92)', fontSize: 12.5, fontWeight: '600', flexShrink: 1},
    glassTileValue: {color: '#FFFFFF', fontSize: 24, fontWeight: '800', letterSpacing: -0.5, marginTop: 6},

    hero: {borderRadius: 28, padding: 20, overflow: 'hidden', marginBottom: 16, shadowOffset: {width: 0, height: 14}, shadowOpacity: 0.28, shadowRadius: 22, elevation: 8},
    heroBlobA: {position: 'absolute', width: 230, height: 230, borderRadius: 115, backgroundColor: 'rgba(255,255,255,0.12)', top: -90, right: -70},
    heroBlobB: {position: 'absolute', width: 170, height: 170, borderRadius: 85, backgroundColor: 'rgba(255,255,255,0.09)', bottom: -70, left: -50},
    heroTop: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
    heroLabel: {color: 'rgba(255,255,255,0.92)', fontSize: 14, fontWeight: '700', flex: 1, paddingRight: 10},
    heroArrow: {width: 32, height: 32, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.24)', alignItems: 'center', justifyContent: 'center'},
    heroValueRow: {flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 8},
    heroValue: {color: '#FFFFFF', fontSize: 36, fontWeight: '800', letterSpacing: -1, flexShrink: 1},
    heroCaption: {color: 'rgba(255,255,255,0.84)', fontSize: 12.5, fontWeight: '600', marginTop: 4},
    heroSpark: {marginTop: 10, marginHorizontal: -4},
    heroTiles: {flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 16},
    heroTileCell: {width: '47.8%', flexGrow: 1},

    rankedRow: {flexDirection: 'row', alignItems: 'center', gap: 12, borderRadius: 24, paddingVertical: 11, paddingHorizontal: 12, marginBottom: 9, minHeight: 64},
    rankedShadow: {borderRadius: 24, marginBottom: 9, shadowColor: BRAND.dark, shadowOffset: {width: 0, height: 8}, shadowOpacity: 0.22, shadowRadius: 14, elevation: 5},
    rankedFlush: {marginBottom: 0},
    rankedText: {flex: 1, minWidth: 0},
    rankedTitle: {fontSize: 15, fontWeight: '700'},
    rankedSub: {fontSize: 12.5, fontWeight: '500', marginTop: 2},
    rankedEnd: {alignItems: 'flex-end', maxWidth: '38%'},
    rankedMeta: {fontSize: 15, fontWeight: '800'},

    actionTile: {borderRadius: 24, borderWidth: 1, padding: 14, minHeight: 118, marginBottom: 12},
    actionTileTop: {flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between'},
    tileBadge: {position: 'absolute', top: -6, right: -8, minWidth: 20, height: 20, borderRadius: 10, paddingHorizontal: 5, backgroundColor: '#EF4444', alignItems: 'center', justifyContent: 'center'},
    tileBadgeText: {color: '#FFFFFF', fontSize: 10.5, fontWeight: '800'},
    actionTileTitle: {fontSize: 15, fontWeight: '700', marginTop: 12},
    actionTileSub: {fontSize: 12, fontWeight: '500', marginTop: 3, lineHeight: 16},

    greeting: {marginBottom: 18},
    greetingTop: {flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 18},
    greetingSpacer: {flex: 1},
    avatarRing: {borderWidth: 2, borderRadius: 26, padding: 2},
    scopeChip: {flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderRadius: 999, paddingHorizontal: 12, height: 34, maxWidth: '48%'},
    scopeChipText: {fontSize: 12.5, fontWeight: '700', flexShrink: 1},
    greetingEyebrow: {fontSize: 14, fontWeight: '600'},
    greetingTitle: {fontSize: 30, fontWeight: '800', letterSpacing: -0.8, marginTop: 2},
    greetingSub: {fontSize: 14.5, fontWeight: '500', marginTop: 6, lineHeight: 20},
});
